import React, { useState } from 'react';
import { SmartphoneIcon, BuildingIcon, GlobeIcon, CreditCardIcon, CheckCircle2, ArrowRight, X } from 'lucide-react';
interface ServicesPageProps {
  onNavigate?: (page: string) => void;
}
export function ServicesPage({
  onNavigate
}: ServicesPageProps) {
  const [activeModal, setActiveModal] = useState<string | null>(null);
  const modalContent = {
    personal: {
      title: 'Personal Top-ups',
      icon: <SmartphoneIcon className="h-8 w-8" data-id="element-247" />,
      content: <div className="space-y-4 text-gray-600" data-id="element-248">
          <p data-id="element-249">
            Our personal top-up service is designed for everyday Kenyans who
            need reliable, instant airtime.
          </p>
          <h4 className="font-bold text-gray-900 mt-6 mb-2" data-id="element-250">
            Supported Networks
          </h4>
          <ul className="list-disc pl-5 space-y-1" data-id="element-251">
            <li data-id="element-252">Safaricom</li>
            <li data-id="element-253">Airtel Kenya</li>
            <li data-id="element-254">Telkom Kenya</li>
            <li data-id="element-255">Equitel</li>
          </ul>
          <h4 className="font-bold text-gray-900 mt-6 mb-2" data-id="element-256">How it works</h4>
          <p data-id="element-257">
            Simply enter your number, choose your amount, and pay securely via
            M-PESA. The airtime reflects on your phone in less than 10 seconds.
          </p>
        </div>
    },
    bulk: {
      title: 'Bulk Purchases',
      icon: <BuildingIcon className="h-8 w-8" data-id="element-258" />,
      content: <div className="space-y-4 text-gray-600" data-id="element-259">
          <p data-id="element-260">
            Streamline your corporate airtime distribution with our
            enterprise-grade bulk purchasing system.
          </p>
          <h4 className="font-bold text-gray-900 mt-6 mb-2" data-id="element-261">
            Enterprise Features
          </h4>
          <ul className="list-disc pl-5 space-y-1" data-id="element-262">
            <li data-id="element-263">Upload Excel/CSV files with hundreds of numbers</li>
            <li data-id="element-264">Schedule recurring top-ups for employees</li>
            <li data-id="element-265">Detailed transaction reporting and analytics</li>
            <li data-id="element-266">API access for custom integrations</li>
          </ul>
          <h4 className="font-bold text-gray-900 mt-6 mb-2" data-id="element-267">
            Volume Discounts
          </h4>
          <p data-id="element-268">
            Businesses purchasing over KES 50,000 monthly qualify for our
            enterprise discount tier.
          </p>
        </div>
    },
    local: {
      title: 'Local Expertise',
      icon: <GlobeIcon className="h-8 w-8" data-id="element-269" />,
      content: <div className="space-y-4 text-gray-600" data-id="element-270">
          <p data-id="element-271">
            We don't just operate in Kenya; we understand the unique challenges
            of the local telecommunications landscape.
          </p>
          <h4 className="font-bold text-gray-900 mt-6 mb-2" data-id="element-272">Our Coverage</h4>
          <p data-id="element-273">
            From the bustling streets of Nairobi to the most remote rural areas,
            if there's network coverage, our top-ups will reach you.
          </p>
          <h4 className="font-bold text-gray-900 mt-6 mb-2" data-id="element-274">
            Local Partnerships
          </h4>
          <p data-id="element-275">
            We work directly with local telcos and payment providers to ensure
            the highest success rates and lowest latency in the market.
          </p>
        </div>
    }
  };
  return <div className="flex flex-col min-h-screen bg-white" data-id="element-276">
      {/* Modal Overlay */}
      {activeModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-277">
          <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity" onClick={() => setActiveModal(null)} data-id="element-278"></div>
          <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in duration-200" data-id="element-279">
            <div className="p-8" data-id="element-280">
              <button onClick={() => setActiveModal(null)} className="absolute top-6 right-6 text-gray-400 hover:text-gray-600 transition-colors bg-gray-50 hover:bg-gray-100 rounded-full p-2" data-id="element-281">
                <X className="h-5 w-5" data-id="element-282" />
              </button>

              <div className="h-16 w-16 bg-emerald-50 rounded-2xl flex items-center justify-center mb-6 text-emerald-600" data-id="element-283">
                {modalContent[activeModal as keyof typeof modalContent].icon}
              </div>

              <h3 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-284">
                {modalContent[activeModal as keyof typeof modalContent].title}
              </h3>

              <div className="prose prose-emerald max-w-none" data-id="element-285">
                {modalContent[activeModal as keyof typeof modalContent].content}
              </div>

              <div className="mt-10 pt-6 border-t border-gray-100 flex justify-end" data-id="element-286">
                <button onClick={() => setActiveModal(null)} className="px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-medium rounded-xl transition-colors" data-id="element-287">
                  Got it
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Hero */}
      <section className="relative py-32 bg-gray-900 overflow-hidden" data-id="element-288">
        <div className="absolute inset-0" data-id="element-289">
          <div className="absolute inset-0 bg-emerald-900/40 mix-blend-multiply z-10" data-id="element-290"></div>
          <img src="https://cdn.magicpatterns.com/uploads/aSX1WwvkFskPr9R4e8Xsp4/pexels-gistsandthrills-4252245.jpg" alt="Two people using smartphones" className="w-full h-full object-cover opacity-60 object-top" data-id="element-291" />
          <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/60 to-transparent z-10" data-id="element-292"></div>
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center z-20 mt-12" data-id="element-293">
          <span className="inline-block py-1 px-3 rounded-full bg-emerald-500/20 text-emerald-300 font-semibold tracking-wider uppercase text-sm mb-6 border border-emerald-400/30 backdrop-blur-sm" data-id="element-294">
            What We Do
          </span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white mb-6 tracking-tight" data-id="element-295">
            Seamless Connectivity <br className="hidden md:block" data-id="element-296" />
            <span className="text-emerald-400" data-id="element-297">For Everyone</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto leading-relaxed" data-id="element-298">
            Comprehensive digital airtime solutions tailored for individuals and
            businesses across Kenya. Fast, reliable, and always on.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-gray-50" data-id="element-299">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-300">
          <div className="text-center mb-16" data-id="element-301">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-302">
              Our Core Services
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-303">
              Everything you need to manage your digital airtime efficiently,
              built with modern technology and local expertise.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12" data-id="element-304">
            {/* Service 1 */}
            <div className="group p-10 rounded-3xl border border-gray-100 bg-white shadow-sm hover:shadow-xl hover:border-emerald-100 transition-all duration-300 flex flex-col h-full" data-id="element-305">
              <div className="h-16 w-16 bg-emerald-50 rounded-2xl flex items-center justify-center mb-8 text-emerald-600 group-hover:scale-110 group-hover:bg-emerald-500 group-hover:text-white transition-all duration-300 shadow-sm" data-id="element-306">
                <SmartphoneIcon className="h-8 w-8" data-id="element-307" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-emerald-700 transition-colors" data-id="element-308">
                Personal Top-ups
              </h3>
              <p className="text-gray-600 leading-relaxed text-lg flex-grow" data-id="element-309">
                Instant airtime recharge for your personal number across all
                major networks (Safaricom, Airtel, Telkom). Enjoy seamless
                transactions and occasional discounts on your daily top-ups.
              </p>
              <div onClick={() => setActiveModal('personal')} className="mt-8 pt-6 border-t border-gray-50 flex items-center text-emerald-600 font-medium group-hover:translate-x-2 transition-transform cursor-pointer" data-id="element-310">
                Learn more <ArrowRight className="ml-2 h-5 w-5" data-id="element-311" />
              </div>
            </div>

            {/* Service 2 */}
            <div className="group p-10 rounded-3xl border border-gray-100 bg-white shadow-sm hover:shadow-xl hover:border-emerald-100 transition-all duration-300 flex flex-col h-full" data-id="element-312">
              <div className="h-16 w-16 bg-emerald-50 rounded-2xl flex items-center justify-center mb-8 text-emerald-600 group-hover:scale-110 group-hover:bg-emerald-500 group-hover:text-white transition-all duration-300 shadow-sm" data-id="element-313">
                <BuildingIcon className="h-8 w-8" data-id="element-314" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-emerald-700 transition-colors" data-id="element-315">
                Bulk Purchases
              </h3>
              <p className="text-gray-600 leading-relaxed text-lg flex-grow" data-id="element-316">
                Ideal for businesses and organizations. Distribute airtime to
                multiple employees or customers simultaneously with our
                efficient, easy-to-use bulk processing system.
              </p>
              <div onClick={() => setActiveModal('bulk')} className="mt-8 pt-6 border-t border-gray-50 flex items-center text-emerald-600 font-medium group-hover:translate-x-2 transition-transform cursor-pointer" data-id="element-317">
                Explore enterprise <ArrowRight className="ml-2 h-5 w-5" data-id="element-318" />
              </div>
            </div>

            {/* Service 3 */}
            <div className="group p-10 rounded-3xl border border-gray-100 bg-white shadow-sm hover:shadow-xl hover:border-emerald-100 transition-all duration-300 flex flex-col h-full" data-id="element-319">
              <div className="h-16 w-16 bg-emerald-50 rounded-2xl flex items-center justify-center mb-8 text-emerald-600 group-hover:scale-110 group-hover:bg-emerald-500 group-hover:text-white transition-all duration-300 shadow-sm" data-id="element-320">
                <GlobeIcon className="h-8 w-8" data-id="element-321" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-emerald-700 transition-colors" data-id="element-322">
                Local Expertise
              </h3>
              <p className="text-gray-600 leading-relaxed text-lg flex-grow" data-id="element-323">
                With a profound understanding of the Kenyan market and its
                diverse telecommunications landscape, we offer a service that
                speaks directly to local needs and challenges.
              </p>
              <div onClick={() => setActiveModal('local')} className="mt-8 pt-6 border-t border-gray-50 flex items-center text-emerald-600 font-medium group-hover:translate-x-2 transition-transform cursor-pointer" data-id="element-324">
                See our coverage <ArrowRight className="ml-2 h-5 w-5" data-id="element-325" />
              </div>
            </div>

            {/* Service 4 */}
            <div className="group p-10 rounded-3xl border border-gray-100 bg-white shadow-sm hover:shadow-xl hover:border-emerald-100 transition-all duration-300 flex flex-col h-full" data-id="element-326">
              <div className="h-16 w-16 bg-emerald-50 rounded-2xl flex items-center justify-center mb-8 text-emerald-600 group-hover:scale-110 group-hover:bg-emerald-500 group-hover:text-white transition-all duration-300 shadow-sm" data-id="element-327">
                <CreditCardIcon className="h-8 w-8" data-id="element-328" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-emerald-700 transition-colors" data-id="element-329">
                Tailored Solutions
              </h3>
              <p className="text-gray-600 leading-relaxed text-lg flex-grow" data-id="element-330">
                We provide customized airtime solutions that cater to your
                specific needs, ensuring a smooth experience whether you're
                managing personal top-ups or complex corporate accounts.
              </p>
              <div onClick={() => onNavigate && onNavigate('contact#contact-info')} className="mt-8 pt-6 border-t border-gray-50 flex items-center text-emerald-600 font-medium group-hover:translate-x-2 transition-transform cursor-pointer" data-id="element-331">
                Get custom quote <ArrowRight className="ml-2 h-5 w-5" data-id="element-332" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lifestyle Section */}
      <section className="py-24 bg-white overflow-hidden" data-id="element-333">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-334">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-335">
            <div className="order-2 lg:order-1 relative" data-id="element-336">
              <div className="absolute -inset-4 bg-emerald-50 rounded-3xl transform -rotate-3 z-0" data-id="element-337"></div>
              <div className="relative z-10 grid grid-cols-2 gap-4 sm:gap-6" data-id="element-338">
                <img src="https://cdn.magicpatterns.com/uploads/vN3DJEgXgu3EisLsj1DTGN/pexels-rdne-10376185.jpg" alt="Man talking on phone" className="rounded-2xl w-full h-64 sm:h-80 object-cover shadow-lg transform translate-y-8" data-id="element-339" />
                <img src="https://cdn.magicpatterns.com/uploads/myTYcW6D3C5TUTvF9Ej6xX/pexels-ninthgrid-2149521550-30689110.jpg" alt="Woman smiling at phone" className="rounded-2xl w-full h-64 sm:h-80 object-cover shadow-lg" data-id="element-340" />
              </div>
            </div>

            <div className="order-1 lg:order-2" data-id="element-341">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 leading-tight" data-id="element-342">
                Empowering connections <br data-id="element-343" />
                <span className="text-emerald-600" data-id="element-344">across the nation</span>
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed" data-id="element-345">
                Whether you're catching up with family, closing a business deal,
                or just browsing the web, we ensure you never run out of airtime
                when it matters most. Our platform is designed to fit seamlessly
                into your daily life.
              </p>

              <ul className="space-y-5" data-id="element-346">
                {['Instant delivery to any network', 'Secure M-PESA integration', '24/7 automated processing', 'Zero hidden fees or extra charges'].map((item, i) => <li key={i} className="flex items-start" data-id="element-347">
                    <CheckCircle2 className="h-6 w-6 text-emerald-500 mr-3 flex-shrink-0" data-id="element-348" />
                    <span className="text-gray-700 font-medium" data-id="element-349">{item}</span>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 bg-emerald-900 text-white relative overflow-hidden" data-id="element-350">
        {/* Decorative background elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-351">
          <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-emerald-800/50 blur-[120px]" data-id="element-352"></div>
          <div className="absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-emerald-800/50 blur-[120px]" data-id="element-353"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-354">
          <div className="text-center mb-20" data-id="element-355">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-356">
              How It Works
            </h2>
            <p className="text-lg text-emerald-100/80 max-w-2xl mx-auto" data-id="element-357">
              Three simple steps to stay connected. Fast, secure, and reliable.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 relative" data-id="element-358">
            {/* Connecting line for desktop */}
            <div className="hidden md:block absolute top-12 left-[16%] right-[16%] h-0.5 bg-emerald-800 z-0" data-id="element-359">
              <div className="absolute top-0 left-0 h-full bg-emerald-400 w-1/2 animate-pulse" data-id="element-360"></div>
            </div>

            <div className="relative z-10 flex flex-col items-center text-center group" data-id="element-361">
              <div className="h-24 w-24 bg-emerald-800 border-4 border-emerald-700 rounded-full flex items-center justify-center text-2xl font-bold text-emerald-300 mb-8 shadow-lg group-hover:scale-110 group-hover:bg-emerald-700 group-hover:border-emerald-500 transition-all duration-300" data-id="element-362">
                1
              </div>
              <h3 className="text-2xl font-bold mb-4" data-id="element-363">Select Network</h3>
              <p className="text-emerald-100/70 leading-relaxed max-w-xs" data-id="element-364">
                Choose your preferred mobile network provider from our supported
                list.
              </p>
            </div>

            <div className="relative z-10 flex flex-col items-center text-center group" data-id="element-365">
              <div className="h-24 w-24 bg-emerald-800 border-4 border-emerald-700 rounded-full flex items-center justify-center text-2xl font-bold text-emerald-300 mb-8 shadow-lg group-hover:scale-110 group-hover:bg-emerald-700 group-hover:border-emerald-500 transition-all duration-300" data-id="element-366">
                2
              </div>
              <h3 className="text-2xl font-bold mb-4" data-id="element-367">Enter Details</h3>
              <p className="text-emerald-100/70 leading-relaxed max-w-xs" data-id="element-368">
                Input the recipient's phone number and your desired airtime
                amount.
              </p>
            </div>

            <div className="relative z-10 flex flex-col items-center text-center group" data-id="element-369">
              <div className="h-24 w-24 bg-emerald-500 border-4 border-emerald-400 rounded-full flex items-center justify-center text-2xl font-bold text-white mb-8 shadow-lg shadow-emerald-500/30 group-hover:scale-110 group-hover:bg-emerald-400 transition-all duration-300" data-id="element-370">
                3
              </div>
              <h3 className="text-2xl font-bold mb-4" data-id="element-371">Confirm Payment</h3>
              <p className="text-emerald-100/70 leading-relaxed max-w-xs" data-id="element-372">
                Pay securely via M-PESA and receive your instant airtime top-up.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>;
}